import React, { useState } from "react";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
import { ChatMessage, SendbirdUser } from "../chat";
import { SVGPlayIcon } from "../icons";
import PlayVideoModal from "./playVideoModal";
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { Popover, List, ListItem, ListItemButton, ListItemText } from "@mui/material";
import { BaseMessage } from "@sendbird/chat/message";
import { BodyChat, DateSeparator, Message, MessageBox, MessageDate, MessageEmbed, MessageOption, VideoMessage } from "../chatStyled";
import { pgVIPPlayedSwing } from "../../mixPanelEvents/mixPanelEvents";
import DownloadIcon from "@mui/icons-material/Download";

dayjs.extend(advancedFormat);

interface ChatBodyProps {
    messages: ChatMessage[];
    user: SendbirdUser;
    deleteMessage: (user: SendbirdUser, message: BaseMessage) => Promise<any>;
    onEditMessage: (messageId: number, oldMessage: string) => void;
    isMobile?: boolean;
    isVipCoachPage?: boolean;
    subscriberUserId?: string;
}

const ChatBody: React.FC<ChatBodyProps> = ({messages, user, onEditMessage, deleteMessage, isMobile, isVipCoachPage, subscriberUserId}) => {
    const [open, setOpen] = useState(false);
    const [currentVideoUrl, setCurrentVideoUrl] = useState<string | undefined>(undefined);
    const [currentImageUrl, setCurrentImageUrl] = useState<string | undefined>(undefined);
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedMessage, setSelectedMessage] = useState<ChatMessage | null>(null);
    
    const handleClickOpen = (msg: ChatMessage) => {
        setCurrentVideoUrl(
            protectFromXSS(msg.raw?.url || "")
        );
        setOpen(true);

        const isCoach = user.user_role === 'coach' || window.pgUser?.is_coach || user.user_id.startsWith('vip_support');
        const isAutoresponder = msg.sender_id === 'autoresponder';
        const isADMM = msg.type === 'ADMM';
        const isOwn = (isAutoresponder || isADMM) ? isCoach : msg.sender_id === user.user_id;
        pgVIPPlayedSwing({
            'Video ID': msg.id,
            Source: isOwn ? 'Uploaded Swings' : 'Coach Videos',
            Type: isCoach ? 'Coach' : 'Member',
        });
    };

    const handleDownloadVideo = async (
    e: React.MouseEvent,
    msg: ChatMessage
) => {
    e.stopPropagation();

    const videoUrl = msg.raw?.url || msg.raw?.plainUrl;

    if (!videoUrl) return;

    const link = document.createElement("a");
    link.href = videoUrl;
    link.download = "video";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

    const handleClose = () => {
        setOpen(false);
        setCurrentVideoUrl(undefined);
        setCurrentImageUrl(undefined);
    };

    const isVideoFile = (msg: ChatMessage) => {
        if (msg.type === 'file') {
            const url = msg.raw?.plainUrl;
            return url && msg.raw.type?.startsWith('video/');
        }
        return false;
    };

    const isImageFile = (msg: ChatMessage) => {
        if (msg.type === 'file') {
            const url = msg.raw?.plainUrl;
            return url && msg.raw.type?.startsWith('image/');
        }
        return false;
    };

    const getThumbnailUrl = (msg: ChatMessage) => {
        if (msg.raw?.thumbnails && msg.raw.thumbnails.length > 0) {
            // Use the first thumbnail
            return msg.raw.thumbnails[0].url;
        }
        return undefined;
    };

    const protectFromXSS = (text: string) => {
        return text
            .replace(/\&/g, '&amp;')
            .replace(/\</g, '&lt;')
            .replace(/\>/g, '&gt;')
            .replace(/\"/g, '&quot;')
            .replace(/\'/g, '&apos;');
    };

    const renderMessageWithLinks = (text: string | null): React.ReactNode => {
        if (!text) return null;
        const urlRegex = /\bhttps?:\/\/\S+/gi;
        const parts = text.split(urlRegex);
        const urls = text.match(urlRegex) || [];
        return parts.reduce<React.ReactNode[]>((acc, part, i) => {
            if (part) acc.push(part);
            if (urls[i]) {
                acc.push(
                    <a
                        key={i}
                        href={urls[i]}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: 'inherit', textDecorationLine: 'underline', wordBreak: 'break-all' }}
                    >
                        {urls[i]}
                    </a>
                );
            }
            return acc;
        }, []);
    };

    const getMessageEmbed = (text: string | null): string | null => {
        if (!text) return null;

        const urls = text.match(/\bhttps?:\/\/\S+/gi);
        if (!urls) return null;

        for (const url of urls) {
            const youtube = url.match(/^.*(youtu\.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/);
            if (youtube && youtube[2].length === 11) {
                return `https://www.youtube.com/embed/${youtube[2]}`;
            }

            const vimeo = url.match(/https?:\/\/(?:www\.|player\.)?vimeo.com\/(?:channels\/(?:\w+\/)?|groups\/([^\/]*)\/videos\/|album\/(\d+)\/video\/|video\/|)(\d+)(?:$|\/|\?)/);
            if (vimeo && vimeo[3]) {
                return `https://player.vimeo.com/video/${vimeo[3]}`;
            }
        }

        return null;
    };

    const handleImageClick = (msg: ChatMessage) => {
        const url = msg.raw?.url;
        if (url) {
            setCurrentImageUrl(url);
            setOpen(true);
        }
    };

    const handleOptionClick = (event: React.MouseEvent<HTMLElement>, msg: ChatMessage) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedMessage(msg);
    }

    const handleOptionClose = () => {
        setAnchorEl(null);
        setSelectedMessage(null);
    }

    const handleEdit = () => {
        console.log('Edit message:', selectedMessage?.id);
        onEditMessage(selectedMessage!.id, selectedMessage!.message || '');
        handleOptionClose();
    }

    const handleRemove = () => {
        deleteMessage(user, selectedMessage!.raw);
        handleOptionClose();
    }

    return (
        <BodyChat $isVipPage={isVipCoachPage}>
            {messages.reduce<React.ReactNode[]>((acc, msg, index) => {
                const msgDate = msg.created_at ? dayjs(msg.created_at).format('MMMM Do, YYYY') : null;
                const nextDate = index < messages.length - 1 && messages[index + 1].created_at
                    ? dayjs(messages[index + 1].created_at).format('MMMM Do, YYYY')
                    : null;

                const isCoach = user.user_role === 'coach' || window.pgUser?.is_coach;
                const isSupport = user.user_role === 'support';
                const isAutoresponder = msg.sender_id === 'autoresponder';
                const isADMM = msg.type === 'admin';
                // A message is from the subscriber if their exact ID matches.
                // No prefix guessing — subscriberUserId is resolved from channel members.
                const isFromMember = !!subscriberUserId && msg.sender_id === subscriberUserId;

                // Coach/support view: own = coach, support, autoresponder, admin (right side).
                // Member view: own = only their own messages; coach/support/auto/admin are left side.
                const isOwn = (isCoach || isSupport)
                    ? !isFromMember
                    : !isAutoresponder && !isADMM && msg.sender_id === user.user_id;
                const embedSrc = !isVideoFile(msg) ? getMessageEmbed(msg.message) : null;

                acc.push(
                    <React.Fragment key={msg.id}>
                    {embedSrc && (
                        <MessageEmbed $own={isOwn}>
                            <iframe
                                src={embedSrc}
                                allow="autoplay; encrypted-media"
                                allowFullScreen
                                title="embed"
                            />
                        </MessageEmbed>
                    )}
                    <MessageBox $own={isOwn} $isVipPage={isVipCoachPage}>
                        {isVideoFile(msg) ?
                        <VideoMessage thumbnail={getThumbnailUrl(msg)} $own={isOwn} $isVipPage={isVipCoachPage}>
                            <span onClick={() => handleClickOpen(msg)}>{SVGPlayIcon}</span>
                            <div
    onClick={(e: React.MouseEvent<HTMLElement>) => handleDownloadVideo(e, msg)}
    style={{
        position: "absolute",
        top: 8,
        right: 8,
        cursor: "pointer",
        zIndex: 2,
    }}
>
<DownloadIcon
  sx={{
    color: "#4A4A4A",
    marginRight:"5px",
    filter: `
      drop-shadow(1px 0 0 white)
      drop-shadow(-1px 0 0 white)
      drop-shadow(0 1px 0 white)
      drop-shadow(0 -1px 0 white)
    `,
  }}
/>
</div>
                            <MessageOption onClick={(e: React.MouseEvent<HTMLElement>) => handleOptionClick(e, msg)}><MoreVertIcon htmlColor="white" /></MessageOption>
                        </VideoMessage> :
                        isImageFile(msg) ?
                        <VideoMessage thumbnail={getThumbnailUrl(msg) || msg.raw?.url} $own={isOwn} $isVipPage={isVipCoachPage} onClick={() => handleImageClick(msg)} style={{ cursor: 'pointer' }}>
                            <MessageOption onClick={(e: React.MouseEvent<HTMLElement>) => { e.stopPropagation(); handleOptionClick(e, msg); }}><MoreVertIcon htmlColor="white" /></MessageOption>
                        </VideoMessage> :
                        <Message $own={isOwn} $isVipPage={isVipCoachPage}>
                            {renderMessageWithLinks(msg.message)}
                            <MessageOption onClick={(e: React.MouseEvent<HTMLElement>) => handleOptionClick(e, msg)}><MoreVertIcon fontSize="small" htmlColor="white" /></MessageOption>
                        </Message>
                        }
                        <MessageDate>
                            {msg.created_at ? dayjs(msg.created_at).format('h:mm A') : ''}
                        </MessageDate>
                    </MessageBox>
                    </React.Fragment>
                );

                // Push separator after the last message of a day group (column-reverse makes it appear above the group)
                if (msgDate && msgDate !== nextDate) {
                    acc.push(
                        <DateSeparator key={`date-${msg.id}`}>{msgDate}</DateSeparator>
                    );
                }

                return acc;
            }, [])}
            <PlayVideoModal
                open={open}
                videoUrl={currentVideoUrl}
                imageUrl={currentImageUrl}
                onClose={handleClose}
                isMobile={isMobile}
            />
            <Popover
                id={'message-option-popover'}
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleOptionClose}
                disableScrollLock={true}
                anchorOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
                slotProps={{
                    paper: {
                        style: {
                            boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.15)',
                        }
                    }
                }}
            >
                <List sx={{ py: 0 }}>
                    {selectedMessage && !isVideoFile(selectedMessage) && (
                        <ListItem disablePadding>
                            <ListItemButton onClick={handleEdit}>
                                <ListItemText primary="Edit" />
                            </ListItemButton>
                        </ListItem>
                    )}
                    <ListItem disablePadding>
                        <ListItemButton onClick={handleRemove}>
                            <ListItemText primary="Remove" />
                        </ListItemButton>
                    </ListItem>
                </List>
            </Popover>
        </BodyChat>
    )
};

export default ChatBody;